import { useState, useEffect, useRef, useContext, useCallback, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  SvgIcon,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { disableNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview";
import { preventUnhandled } from "@atlaskit/pragmatic-drag-and-drop/prevent-unhandled";
import { ExpandableSidebarStateContext } from "@context/shared/ExpandableSidebarState";
import { SIDEBAR_KEYS } from "@config/shared/storage-keys";
import { Scrollbar } from "@components/admin/elements/scrollbar";
import {
  HoverArea,
  SidebarContainer,
  SidebarContent,
  SidebarDivider,
  widths,
} from "./expandable-sidebar.styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle as solidfaCircle,
  faAngleRight,
} from "@fortawesome/pro-solid-svg-icons";
import { faAngleLeft, faCircle } from "@fortawesome/pro-regular-svg-icons";

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

// Read once on init using a safe max (not dependent on media query) for a stable first paint.
// A sidebar with no width of its own yet borrows the shared assets/default
// width — but never a collapsed one: collapsing another page's sidebar must
// not make every brand-new sidebar open collapsed.
const getSavedWidth = (storageKey) => {
  try {
    const keys = [storageKey, SIDEBAR_KEYS.ASSETS, SIDEBAR_KEYS.DEFAULT];
    for (const key of keys) {
      const raw = globalThis.window === undefined ? null : globalThis.localStorage.getItem(key);
      const n = raw ? Number.parseInt(raw, 10) : Number.NaN;
      if (!Number.isFinite(n)) continue;
      const saved = clamp(n, widths.min, widths.max);
      if (key === storageKey || saved > widths.min) return saved;
    }
  } catch { /* localStorage may be unavailable */ }
  return null;
};

export const ExpandableSideBar = forwardRef(({
  topNavHeight,
  children,
  storageKey = SIDEBAR_KEYS.DEFAULT,
  defaultToMax = false,
  scrollable = true,
  startCollapsed = false,
}, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const maxForScreen = isSmallScreen ? 300 : widths.max;

  const { state, setType, setCurrentWidth } = useContext(
    ExpandableSidebarStateContext
  );

  const dividerRef = useRef(null);
  const contentRef = useRef(null);
  const hoverRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  // Initialize from storage synchronously (no flash). With no persisted width,
  // fall back to widths.start — or widths.max when the caller opts in via
  // defaultToMax (the breakpoint clamp below trims it down on small screens).
  // startCollapsed skips the persisted width entirely so the sidebar always
  // opens collapsed, regardless of what a previous session left behind.
  const fallbackWidth = defaultToMax ? widths.max : widths.start;
  const [width, setWidth] = useState(
    () => (startCollapsed ? widths.min : getSavedWidth(storageKey) ?? fallbackWidth),
  );

  // Clamp when breakpoint max changes (do not overwrite with a default)
  useEffect(() => {
    setWidth((prev) => clamp(prev, widths.min, maxForScreen));
  }, [maxForScreen]);

  // Persist to the provided key only. A startCollapsed sidebar never reads
  // the key back, so it must not write it either — the default key is shared
  // with other layouts, which would otherwise open collapsed as well.
  useEffect(() => {
    if (startCollapsed) return;
    try {
      if (globalThis.window !== undefined) localStorage.setItem(storageKey, String(width));
    } catch { /* localStorage may be unavailable */ }
  }, [width, storageKey, startCollapsed]);

  // Keep the shared context width in sync with local width so consumers can react to mount/resize, not just drag.
  useEffect(() => {
    setCurrentWidth(width);
  }, [width, setCurrentWidth]);

  const isCollapsed = width === widths.min;

  const commitWidth = (next) => setWidth(clamp(next, widths.min, maxForScreen));

  const toggleWidth = useCallback(() => {
    commitWidth(width <= widths.start ? maxForScreen : widths.min);
  }, [width, maxForScreen]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleWidth();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        commitWidth(width - 16);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        commitWidth(width + 16);
      }
    },
    [toggleWidth, width]
  );

  // Lightweight hover-to-open (only when collapsed)
  const handleHoverEnter = useCallback(() => {
    if (!isCollapsed) return;
    const t = setTimeout(() => commitWidth(maxForScreen), 400);
    if (hoverRef.current) hoverRef.current.__openTimer = t;
  }, [isCollapsed, maxForScreen]);
  const handleHoverLeave = useCallback(() => {
    const t = hoverRef.current?.__openTimer;
    if (t) clearTimeout(t);
  }, []);

  // --- Atlaskit drag (simplified) ---
  useEffect(() => {
    const el = dividerRef.current;
    if (!el) return;

    return draggable({
      element: el,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
        preventUnhandled.start();
      },
      onDragStart() {
        setType("dragging");
        suppressNextClickRef.current = true; // block the icon button click on mouseup
      },
      onDrag({ location }) {
        const dx = location.current.input.clientX - location.initial.input.clientX;
        const next = clamp(width + dx, widths.min, maxForScreen);
        setCurrentWidth(next);
        contentRef.current?.style.setProperty("--local-resizing-width", `${next}px`);
      },
      onDrop({ location }) {
        preventUnhandled.stop();
        setType("idle");

        const dx = location.current.input.clientX - location.initial.input.clientX;
        const proposed = clamp(width + dx, widths.min, maxForScreen);
        const final = proposed <= widths.start ? widths.min : proposed;

        commitWidth(final);
        setCurrentWidth(final);
        contentRef.current?.style.removeProperty("--local-resizing-width");

        // Allow normal clicks again on the next tick
        setTimeout(() => { suppressNextClickRef.current = false; }, 0);
      },
    });
  }, [width, maxForScreen, setType, setCurrentWidth]);

  const marginTop = "0px";

  return (
    <SidebarContainer ref={ref} className="no-print" role="complementary" aria-label={t("dialogs.resizableSidebar")}>
      <HoverArea
        ref={hoverRef}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
        style={{ display: isCollapsed ? "block" : "none" }}
        aria-hidden={!isCollapsed}
      />
      <SidebarContent
        ref={contentRef}
        isDragging={state.type === "dragging"}
        sx={{
          pointerEvents: state.type === "dragging" ? "none" : "auto",
          "--local-initial-width": `${width}px`,
        }}
      >
        {scrollable ? (
          <Scrollbar
            sx={{
              width: "inherit",
              position: "fixed",
              height: `calc(100vh - ${topNavHeight}px - ${marginTop})`,
              "& .simplebar-content": {
                height: `calc(100vh - ${topNavHeight}px - ${marginTop})`,
              },
              "& .simplebar-scrollbar:before": {
                background: (t) => t.palette.text.disabled,
              },
              visibility: isCollapsed ? "hidden" : "visible",
              marginTop: marginTop,
            }}
          >
            {children}
          </Scrollbar>
        ) : (
          // Self-scrolling children (e.g. RolodexList) manage their own internal
          // scroll region. Wrapping them in the SimpleBar above creates a second,
          // nested scroll container — two scrollbars plus a measurement race that
          // leaves a blank gap. Render them in a plain box instead, so the child's
          // own scroll is the only one. The box carries an explicit viewport-relative
          // height (same as the SimpleBar it replaces): percentage heights do NOT
          // resolve through the flex-stretched SidebarContent chain, so `height:100%`
          // collapses the child to content height and overflows the page.
          <Box
            sx={{
              width: "inherit",
              height: `calc(100vh - ${topNavHeight}px - ${marginTop})`,
              overflow: "hidden",
              visibility: isCollapsed ? "hidden" : "visible",
            }}
          >
            {children}
          </Box>
        )}
      </SidebarContent>

      <SidebarDivider
        ref={dividerRef}
        role="separator"
        aria-orientation="vertical"
        aria-label={t("dialogs.resizeSidebar")}
        tabIndex={0}
        isCollapsed={isCollapsed}
        isDragging={state.type === "dragging"}
        onKeyDown={onKeyDown}
        onDoubleClick={toggleWidth}
      >
        <IconButton
          className="icon-container"
          size="small"
          onClick={() => {
            if (suppressNextClickRef.current) {
              suppressNextClickRef.current = false;
              return;
            }
            toggleWidth();
          }}
          aria-label={isCollapsed ? t("dialogs.expandSidebar") : t("dialogs.collapseSidebar")}
        >
          <SvgIcon className="circleIcon">
            <FontAwesomeIcon icon={solidfaCircle} />
          </SvgIcon>
          <SvgIcon className="regularCircleIcon">
            <FontAwesomeIcon icon={faCircle} />
          </SvgIcon>
          <SvgIcon className="angleIcon">
            {isCollapsed ? (
              <FontAwesomeIcon icon={faAngleRight} />
            ) : (
              <FontAwesomeIcon icon={faAngleLeft} />
            )}
          </SvgIcon>
        </IconButton>
      </SidebarDivider>
    </SidebarContainer>
  );
});

ExpandableSideBar.displayName = "ExpandableSideBar";

ExpandableSideBar.propTypes = {
  // Used by both paths: the scrollable SimpleBar and the non-scrollable box both
  // size their height to `calc(100vh - topNavHeight)`.
  topNavHeight: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  storageKey: PropTypes.string,
  defaultToMax: PropTypes.bool,
  scrollable: PropTypes.bool,
  startCollapsed: PropTypes.bool,
};
